import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import {CommonModal} from "@app/components/modals/CommonModal"
import { Button, Separator, useModal } from "@shortsworks/ui";
import { useFormWithSchema } from "@app/components/forms/hooks/useFormWithSchema";
import { ShopSettingsSchema } from "@app/pages/settings/Shop/shopSettingsSchema";
import {
  useGetAccountPlatform,
  useUpdateAccountPlatform,
  getProductListForAuthCheck,
  useGetAccount, useGetProductListForAuthCheck,
} from "@shortsworks/user-api"
import {LoaderCircle} from "lucide-react"
import { lazy, Suspense, useEffect, useState } from "react";
import FormContainer from "src/components/forms/container/FormContainer";
import { MainContentContainer } from "@app/components/MainContainer";
import { MainHeader } from "@app/components/MainContainer/MainHeader";
import { MainTitle } from "@app/components/MainContainer/MainTitle";
import { MainDescription } from "@app/components/MainContainer/MainDescription";
import { MainContent } from "@app/components/MainContainer/MainContent";

export const ShopSettings = () => {
  const { data: accountData, isSuccess: isSuccessGetAccount } = useGetAccount();
  const { data: accountPlatformData, isSuccess: isSuccessGetAccountPlatform } =
    useGetAccountPlatform();
  const {
    mutateAsync: mutateAsyncUpdateAccountPlatform,
    isPending: isPendingUpdateAccountPlatform,
  } = useUpdateAccountPlatform();

  const { form, schemas, parseForm, watchForm, setFormValues } =
    useFormWithSchema({
      formSchemas: ShopSettingsSchema,
      mode: "onChange",
    });

  const [isVerified, setIsVerified] = useState(false);
  const { showModal } = useModal();

  useEffect(() => {
    if (!isSuccessGetAccountPlatform || !isSuccessGetAccount) {
      return;
    }
    //@ts-ignore
    const { shopNo, url, platformId, platformType, name, email } =
      accountPlatformData.data;
    const { name: platformName } = accountData.data;

    setFormValues({
      platformName,
      shopNo: shopNo.toString(),
      platformUrl: url,
      platformId,
      platformType,
      email,
      name,
    });
  }, [isSuccessGetAccountPlatform, isSuccessGetAccount]);

  watchForm(() => {
    if (isVerified) {
      return setIsVerified(false);
    }
  });

  const handleVerify = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // getQuery의 params가 들어갈 때 isPending 정보를 가져오기 어려움 (state 필요)
    const button = event.target as HTMLButtonElement;
    button.disabled = true;

    if (!isSuccessGetAccountPlatform) {
      return showModal(CommonErrorModal, {title: "플랫폼 정보를 가져오는 데 실패했습니다."});
    }
    const { success, data } = parseForm();
    if (!success) return;

    const { apiKey } = accountPlatformData.data;
    const { platformType, platformId, shopNo } = data;

    try {
      const result = await getProductListForAuthCheck({
        platformType,
        platformId,
        shopNo,
        apiKey,
      });

      button.disabled = false;
      if (!result.data.products) {
        return showModal(CommonErrorModal, {
          title: "연동하기가 실패했습니다.",
        });
      }

      showModal(CommonModal, {title: "연동 완료", description: "연동이 완료되었습니다. 저장하기를 눌러주세요."});
      return setIsVerified(true);

    } catch (error) {
      button.disabled = false;
      return showModal(CommonErrorModal, { title: "연동하기가 실패했습니다." });
    }
  };

  const handleSubmit = async () => {
    if (!isSuccessGetAccountPlatform) return;
    const { success, data } = parseForm();
    const { apiKey } = accountPlatformData.data;

    if (!success) {
      return;
    }
    const { platformId, platformName, platformUrl, name, email } = data;
    const result = await mutateAsyncUpdateAccountPlatform({
      data: {
        platformId,
        platformName: name,
        platformUrl,
        name: platformName,
        email,
        apiKey,
      },
    });

    if (!result.data.success) {
      return showModal(CommonErrorModal, {
        title: "쇼핑몰 정보 변경을 실패했습니다.",
      });
    }

    showModal(CommonErrorModal, { title: "쇼핑몰 정보가 변경되었습니다." });
    return setIsVerified(false);
  };

  if(!isSuccessGetAccount || !isSuccessGetAccountPlatform) {
    return (
        <div className="flex flex-shrink items-center justify-center w-full h-full">
          <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
        </div>
    );
  }

  return (
    <MainContentContainer>
      <MainHeader>
        <MainTitle>쇼핑몰 정보</MainTitle>
        <MainDescription>쇼핑몰 정보를 관리할 수 있습니다.</MainDescription>
      </MainHeader>
      <MainContent>
            <Suspense>
              <FormContainer schemas={schemas} form={form} />
            </Suspense>
            <Button
              className="mt-4 mr-2"
              type="button"
              variant="destructive"
              onClick={handleVerify}
              disabled={isVerified}
            >
              연동하기
            </Button>
            <Button
              className="mt-4"
              type="submit"
              onClick={handleSubmit}
              disabled={!isVerified || isPendingUpdateAccountPlatform}
            >
              저장하기
            </Button>
      </MainContent>
    </MainContentContainer>
  );
};
